import React from 'react';
import { Container, Typography, Box, Paper, Link, Divider } from '@mui/material';
import { useTenant } from '../contexts/TenantContext';

const PrivacyPolicy: React.FC = () => {
  const { tenantConfig } = useTenant();
  const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW Ideas Platform' : 'Severance Town Center Ideas Platform';
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
          Last Updated: {currentDate}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to {projectName}. We are committed to protecting your privacy and being transparent about how we collect and use your information. This platform is designed to facilitate community engagement in local planning and development projects. We do not sell, rent, or trade your personal information to third parties.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              2. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              When you create an account or use our platform, we may collect the following information:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li><strong>Account Information:</strong> Your name, email address, and password (encrypted)</li>
                <li><strong>Profile Information:</strong> Your connection to the project area (e.g., resident, business owner, visitor)</li>
                {tenantConfig?.tenant_id === 'evanston' && (
                  <li><strong>Location Information (Evanston only):</strong> Your zip code and ward number to help us understand community representation</li>
                )}
                <li><strong>Content:</strong> Ideas, comments, and feedback you submit to the platform</li>
                <li><strong>Usage Data:</strong> Information about how you interact with the platform (e.g., pages visited, features used)</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, and similar technical information</li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li>Provide and maintain the community engagement platform</li>
                <li>Display your name alongside ideas and comments you post publicly</li>
                <li>Understand community demographics and ensure diverse representation</li>
                <li>Communicate with you about platform updates and community events</li>
                <li>Improve and optimize the platform based on usage patterns</li>
                <li>Prevent fraud, abuse, and ensure platform security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              4. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>We do not sell your personal information.</strong> We may share your information only in the following circumstances:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li><strong>Public Content:</strong> Ideas, comments, and your display name are visible to all platform users and may be included in public reports about community input</li>
                <li><strong>Project Partners:</strong> We may share your data with local government officials and planning partners to inform project decisions</li>
                <li><strong>Service Providers:</strong> We use Firebase (Google) for authentication and Google Cloud Platform for hosting. These providers have access to certain data only to perform services on our behalf and are obligated to protect your information</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect the rights, property, or safety of our users or others</li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              5. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement reasonable security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. This includes:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li>Encrypted password storage</li>
                <li>Secure HTTPS connections</li>
                <li>Regular security updates and monitoring</li>
                <li>Limited employee access to personal data</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph>
              However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              6. Your Rights and Choices
            </Typography>
            <Typography variant="body1" paragraph>
              You have the following rights regarding your personal information:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li><strong>Access:</strong> You can view and update your profile information at any time</li>
                <li><strong>Correction:</strong> You can correct inaccurate information in your profile</li>
                <li><strong>Deletion:</strong> You can request deletion of your account by contacting us (note that publicly posted ideas and comments may remain visible with your name removed)</li>
                <li><strong>Opt-out:</strong> You can opt out of non-essential communications</li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              7. Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use essential cookies to maintain your login session and remember your preferences. We do not use third-party advertising cookies or sell your browsing data to advertisers.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our platform is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              9. Changes to This Privacy Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the platform after such changes constitutes your acceptance of the updated policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              10. Third-Party Services
            </Typography>
            <Typography variant="body1" paragraph>
              Our platform uses the following third-party services:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li><strong>Firebase Authentication (Google):</strong> For secure user authentication and account management</li>
                <li><strong>Google Cloud Platform:</strong> For hosting and data storage</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph>
              These services have their own privacy policies governing their use of your information. We encourage you to review their policies:
            </Typography>
            <Typography component="div" variant="body1">
              <ul style={{ marginTop: 0 }}>
                <li>
                  <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                    Google Privacy Policy
                  </Link>
                </li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              11. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Email:</strong> {tenantConfig?.support_email}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 4 }}>
              This platform is operated as a community engagement tool to gather input for local development projects. We are committed to using your information responsibly and transparently to support community-driven planning.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;

